import { ScopeDIProvider, requireService, wrap } from '@repo/di-ez';
import { useGridApiRef } from '@repo/go-table';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';

import { TrajectoryGraph, TrajectoryTable } from './features';
import { TrajectoryActionsBar } from './features/trajectory-actions-bar';
import { TrajectoryEmptyState } from './features/trajectory-empty-state';
import { TrajectoryGraphStore } from './features/trajectory-graph/trajectory-graph.store';
import { TrajectoryListSidebar } from './features/trajectory-list-sidebar';
import { TrajectorySettingsSidebar } from './features/trajectory-settings-sidebar';
import { DEFAULT_USE_SHORT_COLUMN_HEADERS } from './features/trajectory-table/display-manager';
import { useTrajectoryColumns } from './features/trajectory-table/hooks/use-trajectory-columns';
import { TrajectoryI18nProvider } from './i18n/TrajectoryI18nProvider';
import { TrajectoryUiStore } from './trajectory-ui.store';
import styles from './trajectory.module.scss';
import type { TrajectoryWidgetProps } from './types/external-services';

const TrajectoryWidgetContent = wrap(function TrajectoryWidgetContent({
  language,
  showTableToolbar,
  extraActionsBarItems,
  mainHeader,
  isEditing = false,
}: Pick<TrajectoryWidgetProps, 'language' | 'showTableToolbar' | 'extraActionsBarItems' | 'mainHeader' | 'isEditing'>) {
  const trajectoryService = requireService('trajectoryService');
  const uiStore = requireService('trajectoryUiStore');
  const tableApi = useGridApiRef();
  const columns = useTrajectoryColumns(
    uiStore.displayManager.useShortColumnHeaders ?? DEFAULT_USE_SHORT_COLUMN_HEADERS,
  );

  const showContent =
    trajectoryService.hasWell && trajectoryService.hasWellboreSelection && trajectoryService.hasTrajectorySelection;

  return (
    <>
      <div className={styles.trajectoryMain}>
        {mainHeader}
        {!showContent ? (
          <TrajectoryEmptyState />
        ) : (
          <div className={clsx(styles.trajectoryContent, styles[`trajectoryContent${uiStore.expandState}`])}>
            <div className={styles.tableWrapper}>
              <TrajectoryTable language={language} columns={columns} tableApi={tableApi} />
            </div>
            <div className={styles.graphWrapper}>
              <div className={styles.graphPanel}>
                <TrajectoryGraph />
              </div>
            </div>
          </div>
        )}
      </div>
      {showTableToolbar && (
        <div className={styles.actionsBarWrapper}>
          <TrajectoryActionsBar extraTopItems={extraActionsBarItems} />
        </div>
      )}
      <TrajectorySettingsSidebar columns={columns} tableApi={tableApi} />
      <TrajectoryListSidebar isEditing={isEditing} />
    </>
  );
});

export const TrajectoryWidget = wrap(function TrajectoryWidget({
  trajectoryService,
  notifications,
  language,
  parentI18n,
  initialState,
  onStateChange,
  isTableSettingsOpen,
  isTableCheckboxesVisible,
  isTrajectoryListOpen,
  isEditing = false,
  onTableSettingsOpenChange,
  onTableCheckboxesVisibleChange,
  onTrajectoryListOpenChange,
  showTableToolbar = false,
  extraActionsBarItems,
  mainHeader,
  settingsDrawerContainer,
}: TrajectoryWidgetProps) {
  const [uiStore] = useState(() => new TrajectoryUiStore(initialState));
  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);
  const [trajectoryGraphStore] = useState(() => {
    const store = new TrajectoryGraphStore(trajectoryService);

    if (initialState?.chartDimension) {
      store.setChartDimension(initialState.chartDimension);
    }

    if (initialState?.trajectory3dViewControl) {
      store.setTrajectory3dViewControl(initialState.trajectory3dViewControl);
    }

    if (initialState?.chart2dSettings) {
      store.setChart2dSettings(initialState.chart2dSettings);
    }

    return store;
  });

  useEffect(() => {
    uiStore.bindGraphStore(trajectoryGraphStore);
  }, [uiStore, trajectoryGraphStore]);

  useEffect(() => {
    uiStore.setExternalCallbacks({
      onStateChange,
      isTableSettingsOpen,
      isTableCheckboxesVisible,
      isTrajectoryListOpen,
      onTableSettingsOpenChange,
      onTableCheckboxesVisibleChange,
      onTrajectoryListOpenChange,
    });
  }, [
    uiStore,
    onStateChange,
    isTableSettingsOpen,
    isTableCheckboxesVisible,
    isTrajectoryListOpen,
    onTableSettingsOpenChange,
    onTableCheckboxesVisibleChange,
    onTrajectoryListOpenChange,
  ]);

  useEffect(() => {
    // Explicit prop wins; otherwise dashboard (actions bar) scopes drawer to the widget root.
    if (settingsDrawerContainer !== undefined) {
      uiStore.setSettingsDrawerContainer(settingsDrawerContainer);
      return;
    }

    uiStore.setSettingsDrawerContainer(showTableToolbar ? wrapperElement : null);
  }, [uiStore, settingsDrawerContainer, showTableToolbar, wrapperElement]);

  const init = useCallback(() => {
    return {
      trajectoryService,
      trajectoryUiStore: uiStore,
      trajectoryGraphStore,
      trajectoryNotifications: notifications,
    };
  }, [trajectoryService, uiStore, trajectoryGraphStore, notifications]);

  return (
    <TrajectoryI18nProvider language={language} parentI18n={parentI18n}>
      <ScopeDIProvider init={init}>
        <div
          ref={setWrapperElement}
          className={clsx(styles.trajectoryWrapper, showTableToolbar && styles.trajectoryWrapperWithActionsBar)}
        >
          <TrajectoryWidgetContent
            language={language}
            showTableToolbar={showTableToolbar}
            extraActionsBarItems={extraActionsBarItems}
            mainHeader={mainHeader}
            isEditing={isEditing}
          />
        </div>
      </ScopeDIProvider>
    </TrajectoryI18nProvider>
  );
});

declare module '@repo/di-ez' {
  interface ServicesCollectionMap {
    trajectoryService: import('./service/trajectory-service').TrajectoryService;
    trajectoryUiStore: TrajectoryUiStore;
    trajectoryGraphStore: TrajectoryGraphStore;
    trajectoryNotifications: import('./types/host-services').ITrajectoryNotificationsService;
  }
}
